import { useLayoutEffect, useRef, useState } from 'react';

/** Don't shrink past this — below it the labels stop being legible, and we'd
 *  rather let a freak value hug the edge than vanish. */
const MIN_SCALE = 0.62;

/** A side block may borrow this fraction of the column gap toward the price —
 *  the gap is dead space, so lending half of it lets the side text stay larger
 *  while still leaving a visible margin before the hero. */
const GAP_BORROW = 0.5;

/**
 * When a long Max Pull value (e.g. "$7,500") eats the row, scale the stats row
 * DOWN instead of letting the "Select Odds" text ride over the hero. The driving
 * factor comes from the side blocks (`scale`) — the hero stays `shrink-0`, so
 * it's the sides flex squeezes. The hero gets a SEPARATE, gentler factor
 * (`heroScale`, halfway back to full) so it shrinks enough to stop dwarfing the
 * rest but still reads as the focal figure. Their visual size never affects the
 * fit — `transform` doesn't touch layout — so the two can diverge safely.
 *
 * Each side has an OUTER column (the flex slot — its `clientWidth` is the width
 * flex allotted) and an INNER block (the actual content — its `offsetWidth` is
 * the natural width, transform-independent so reading it never loops with the
 * scale we apply). We measure `offsetWidth` against `clientWidth`, NOT the
 * column's `scrollWidth`: a flex column with `min-w-0` clips `scrollWidth` to
 * its own box and an `items-end` column's overflow runs the wrong way, so
 * `scrollWidth` reads equal to `clientWidth` and the fit silently no-ops.
 *
 * Both sides share ONE scale — the smaller of the two — so they stay visually
 * symmetric. Re-measures on any column resize (rotation / breakpoint / iframe).
 *
 * Apply `scale` via `transform: scale(...)`, origin pinned to each block's
 * outer/bottom corner (sides → row edge, hero → centre) so they collapse the
 * right way and keep their baselines aligned.
 */
export function useFitSideText(deps: string): {
  leftColRef: React.RefObject<HTMLDivElement | null>;
  leftInnerRef: React.RefObject<HTMLDivElement | null>;
  rightColRef: React.RefObject<HTMLDivElement | null>;
  rightInnerRef: React.RefObject<HTMLDivElement | null>;
  scale: number;
  heroScale: number;
} {
  const leftColRef = useRef<HTMLDivElement | null>(null);
  const leftInnerRef = useRef<HTMLDivElement | null>(null);
  const rightColRef = useRef<HTMLDivElement | null>(null);
  const rightInnerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const leftCol = leftColRef.current;
    const leftInner = leftInnerRef.current;
    const rightCol = rightColRef.current;
    const rightInner = rightInnerRef.current;
    if (!leftCol || !leftInner || !rightCol || !rightInner) return;

    const fit = () => {
      // The row owns the column gap; a side may grow into part of it.
      const gap = parseFloat(getComputedStyle(leftCol.parentElement ?? leftCol).columnGap) || 0;
      const avail = (col: HTMLDivElement) => col.clientWidth + gap * GAP_BORROW;
      const factorFor = (col: HTMLDivElement, inner: HTMLDivElement) =>
        inner.offsetWidth > 0 ? avail(col) / inner.offsetWidth : 1;
      const next = Math.min(1, factorFor(leftCol, leftInner), factorFor(rightCol, rightInner));
      setScale(next < MIN_SCALE ? MIN_SCALE : next);
    };

    fit();

    const ro = new ResizeObserver(fit);
    ro.observe(leftCol);
    ro.observe(rightCol);
    return () => ro.disconnect();
  }, [deps]);

  // The hero shrinks only halfway down to the side factor — crisp focal size
  // without dwarfing the rest.
  const heroScale = scale + (1 - scale) * 0.5;

  return { leftColRef, leftInnerRef, rightColRef, rightInnerRef, scale, heroScale };
}
